import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Quote, Star, ChevronLeft, ChevronRight } from "lucide-react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { useRef, useState, useEffect, useCallback } from "react";

const Testimonials = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(1);

  const testimonials = [
    {
      name: "Norbert Wieschalla",
      role: "Unternehmer",
      text: "Ich hatte das Vergnügen, mit Maria Keutel zusammenzuarbeiten, und bin absolut begeistert von ihrer Professionalität, Kompetenz und Effizienz. Maria ist nicht nur fachlich top aufgestellt, sondern arbeitet auch äußerst zügig und lösungsorientiert. Ihr freundlicher und unterstützender Umgang macht die Zusammenarbeit besonders angenehm – sie geht stets auf individuelle Bedürfnisse ein und bietet hilfreiche Impulse. Besonders beeindruckt hat mich ihre Fähigkeit, komplexe Themen wie KI-Tools oder Gründungsstrategien praxisnah und verständlich zu vermitteln. Durch ihre Beratung konnte ich wertvolle Zeit sparen und meine Projekte deutlich strukturierter angehen. Maria denkt kritisch mit, hinterfragt sinnvoll und entwickelt dabei kreative Lösungen, die wirklich weiterhelfen. Langfristige Zusammenarbeit? Auf jeden Fall! Ich schätze ihre Expertise sehr und plane definitiv, auch in Zukunft mit Maria zu arbeiten. Wer eine kompetente, engagierte und sympathische Ansprechpartnerin sucht – sei es für Gründungscoaching, KI-Schulungen oder strategische Beratung – ist bei ihr goldrichtig. Fazit: 5 Sterne und eine klare Empfehlung! Vielen Dank, Maria, für deine tolle Unterstützung.",
      rating: 5
    },
    {
      name: "Steffen Braun",
      role: "Gründer (BAFA-Coaching)",
      text: "Die Beratung wird über BAFA finanziert und hat es in sich. Im 1:1 Coaching mit Maria lerne ich viele wichtige Fähigkeiten für den Unternehmensstart, die ich gleich umsetzen kann. Ich kann Bizzmade nur empfehlen.",
      rating: 5
    },
    {
      name: "Robert Musiol",
      role: "Gründer (IHK-Vorgründungscoaching)",
      text: "Ich bin mit der Gründungsberatung im Rahmen des IHK-Vorgründungscoachings rundum zufrieden! Die Zusammenarbeit war nicht nur professionell, sondern auch sehr unterstützend und inspirierend. Besonders hilfreich war die strukturierte Begleitung bei der Erstellung meines Businessplans sowie beim Pitch Deck und Read Deck, die eine wichtige Grundlage für meine weitere Investorensuche bilden. Ich habe mich bestens beraten gefühlt – vielen Dank für die tolle Unterstützung und die wertvollen Impulse für meine Gründung!",
      rating: 5
    },
    {
      name: "Dr. med. Ramona Müller",
      role: "Ärztin",
      text: "Maria hat eine sehr angenehme, äußerst menschliche Art, komplexe Zusammenhänge zu vermitteln. Zu jeder Zeit unterstützend und ansprechbar – und immer auf Augenhöhe.",
      rating: 5
    }
  ];

  const next = useCallback(() => {
    setDirection(1);
    setCurrent((c) => (c + 1) % testimonials.length);
  }, [testimonials.length]);

  const prev = useCallback(() => {
    setDirection(-1);
    setCurrent((c) => (c - 1 + testimonials.length) % testimonials.length);
  }, [testimonials.length]);

  useEffect(() => {
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, [next]);

  const variants = {
    enter: (dir: number) => ({ x: dir > 0 ? 300 : -300, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir: number) => ({ x: dir > 0 ? -300 : 300, opacity: 0 }),
  };

  return (
    <section id="testimonials" className="py-32 bg-background relative overflow-hidden">
      <div className="absolute inset-0 bg-dot-pattern opacity-[0.03]" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative" ref={ref}>
        <motion.div
          className="text-center mb-20"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <motion.h2
            className="text-4xl md:text-5xl lg:text-6xl font-extrabold gradient-heading mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Was Kund:innen sagen
          </motion.h2>
          <motion.div
            className="w-24 h-1 bg-gradient-to-r from-accent to-purple-accent mx-auto rounded-full mb-8"
            initial={{ scaleX: 0 }}
            animate={isInView ? { scaleX: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          />
          <motion.p
            className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto leading-relaxed"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            Vertrauen Sie auf die Erfahrungen zufriedener Klient:innen, die mit
            meiner Unterstützung ihre Ziele erreicht haben.
          </motion.p>
        </motion.div>

        {/* Carousel */}
        <div className="relative max-w-4xl mx-auto mb-16">
          <div className="overflow-hidden min-h-[320px] md:min-h-[280px] flex items-center">
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={current}
                custom={direction}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.4, ease: "easeInOut" }}
                className="w-full"
              >
                <Card className="border-2 border-border/50 shadow-[var(--shadow-large)] bg-gradient-card relative overflow-hidden">
                  <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-accent to-purple-accent" />
                  <CardContent className="p-10 lg:p-14">
                    <Quote className="w-10 h-10 text-accent/20 mb-6" />

                    <div className="flex mb-5 gap-1">
                      {[...Array(testimonials[current].rating)].map((_, i) => (
                        <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                      ))}
                    </div>

                    <p className="text-muted-foreground mb-8 italic leading-relaxed text-base md:text-lg">
                      &ldquo;{testimonials[current].text}&rdquo;
                    </p>

                    <div className="border-t-2 border-border/50 pt-5">
                      <p className="font-semibold text-foreground text-lg">{testimonials[current].name}</p>
                      <p className="text-sm text-muted-foreground">{testimonials[current].role}</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation arrows */}
          <button
            onClick={prev}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-14 w-10 h-10 rounded-full bg-card border-2 border-border/50 shadow-[var(--shadow-soft)] flex items-center justify-center hover:border-accent/50 transition-colors duration-200"
            aria-label="Vorheriges Testimonial"
          >
            <ChevronLeft className="w-5 h-5 text-muted-foreground" />
          </button>
          <button
            onClick={next}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-14 w-10 h-10 rounded-full bg-card border-2 border-border/50 shadow-[var(--shadow-soft)] flex items-center justify-center hover:border-accent/50 transition-colors duration-200"
            aria-label="Nächstes Testimonial"
          >
            <ChevronRight className="w-5 h-5 text-muted-foreground" />
          </button>

          {/* Dots */}
          <div className="flex justify-center gap-2 mt-8">
            {testimonials.map((_, i) => (
              <button
                key={i}
                onClick={() => { setDirection(i > current ? 1 : -1); setCurrent(i); }}
                className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                  i === current ? "bg-accent w-8" : "bg-border hover:bg-muted-foreground/30"
                }`}
                aria-label={`Testimonial ${i + 1}`}
              />
            ))}
          </div>
        </div>

        <motion.div
          className="text-center"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={isInView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <div className="bg-accent/5 rounded-2xl p-12 max-w-3xl mx-auto border-2 border-accent/20 hover:border-accent/40 transition-all duration-300 shadow-[var(--shadow-soft)] relative overflow-hidden group">
            <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-accent to-purple-accent opacity-50 group-hover:opacity-100 transition-opacity duration-300" />
            <motion.div whileHover={{ scale: 1.05, rotate: 2 }} transition={{ duration: 0.2 }}>
              <Quote className="w-16 h-16 text-accent mx-auto mb-6" />
            </motion.div>
            <h3 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
              Werden Sie Teil der Erfolgsgeschichten
            </h3>
            <p className="text-lg text-muted-foreground mb-8 max-w-xl mx-auto">
              Lassen Sie uns gemeinsam an Ihrem unternehmerischen Erfolg arbeiten.
              Vereinbaren Sie jetzt Ihr kostenloses Erstgespräch.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button variant="cta" size="lg" className="min-h-[44px]" asChild>
                <a href="/#contact">Erstgespräch vereinbaren</a>
              </Button>
              <Button variant="outline" size="lg" className="border-2 min-h-[44px]" asChild>
                <a href="/#testimonials">Referenzen ansehen</a>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Testimonials;
